/**
 * InventoriesTable Section Component
 *
 * Composes DataTableProvider + DataTableContent with inventory column definitions.
 * Handles server-side pagination and row click navigation.
 */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { DataTableProvider } from '../../DataTable/DataTableProvider';
import { DataTableContent } from '../../DataTable/DataTableContent';
import { createInventoryColumns } from '../columns';
import { useUpdateInventoryStatus } from '@hooks/useInventories';
import type { InventoriesTableProps } from '../types';

export function InventoriesTable({
  inventories,
  currentStatus,
  isLoading = false,
  isFetching = false,
  onRowClick,
  sorting,
  onSortingChange,
  className,
}: InventoriesTableProps) {
  const updateStatus = useUpdateInventoryStatus();
  const navigate = useNavigate();

  const columns = useMemo(
    () => createInventoryColumns({
      status: currentStatus,
      onStatusToggle: (id, active) => updateStatus.mutate({ id, active }),
      onEdit: (id) => navigate({ to: '/inventories/$id/edit', params: { id: String(id) } }),
    }),
    [currentStatus, updateStatus, navigate],
  );

  return (
    <div className={cn('flex flex-col', className)}>
      <DataTableProvider
        columns={columns}
        data={inventories}
        isLoading={isLoading}
        isFetching={isFetching}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={onSortingChange}
      >
        <DataTableContent
          onRowClick={onRowClick}
          emptyTitle="No inventory found"
        />
      </DataTableProvider>
    </div>
  );
}
